/**
 * Repositorio «Redes Sociales»: consulta de los Sheets de métricas de redes que viven en
 * Drive, leídos en su lugar por el motor. Aquí están la cabecera, los filtros globales (red,
 * perfil, periodo), la carga de los datasets mensuales y de fotos de seguidores, y las
 * pestañas Resumen, Análisis, Publicaciones y Seguidores.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ink, studio } from '../admin/studioUi';
import { useIsNarrow } from '../../useMediaQuery';
import { useAuth } from '../../AuthContext';
import { useData } from '../../DataContext';
import { clientGrouping } from './analysisUtils';
import InfoTip from './InfoTip';
import { fetchCatalog, queryRepository, refreshRepository } from './repoApi';
import {
    NETWORK_ORDER, PERIOD_PRESETS, POST_METRICS, inPeriod, isBlank, matchesSelection, mergeMonthly, monthKey,
    periodLabel, profileCatalog, resolvePeriod,
} from './socialRepoUtils';
import { loadKpis, prepareKpi, saveKpis } from './customKpis';
import KpiManager from './KpiManager';
import LoadingBlock from './LoadingBlock';
import SocialSummary from './SocialSummary';
import SocialPosts from './SocialPosts';
import SocialFollowers from './SocialFollowers';
import SocialAnalysis from './SocialAnalysis';
import NetworkDot from './NetworkDot';

const REPO = 'social';
const TABS = [
    { id: 'summary', label: 'Resumen' },
    { id: 'analysis', label: 'Análisis' },
    { id: 'posts', label: 'Publicaciones' },
    { id: 'followers', label: 'Seguidores' },
];

/** Consultas base: tablas mensuales y fotos completas (son chicas y permiten comparar periodos). */
const BASE_QUERIES = [
    {
        id: 'postsMonthly',
        dataset: 'postsMonthly',
        dimensions: ['Mes', 'Red', 'Perfil'],
        metrics: Object.fromEntries(['Publicaciones', ...POST_METRICS].map(m => [m, 'sum'])),
    },
    {
        id: 'followersMonthly',
        dataset: 'followersMonthly',
        dimensions: ['Mes', 'Red', 'Perfil'],
        metrics: { 'Seguidores al Cierre': 'max', Nuevos: 'sum', Perdidos: 'sum', Neto: 'sum' },
    },
    {
        id: 'followerSnapshots',
        dataset: 'followerSnapshots',
        dimensions: ['Fecha', 'Red', 'Perfil'],
        metrics: { Seguidores: 'max' },
    },
];

const VIEW_PREFIX = 'cm-repo-view:';

/** Filtros y pestaña recordados por usuario en este navegador (comodidad, no es dato compartido). */
function readView(uid) {
    try {
        return JSON.parse(window.localStorage.getItem(`${VIEW_PREFIX}${REPO}:${uid || 'anon'}`) || '{}') || {};
    } catch (error) {
        return {};
    }
}

function writeView(uid, view) {
    try {
        window.localStorage.setItem(`${VIEW_PREFIX}${REPO}:${uid || 'anon'}`, JSON.stringify(view));
    } catch (error) {
        /* Sin almacenamiento disponible: los filtros duran solo esta visita. */
    }
}

const clean = (rows) => (rows || [])
    .filter(row => !isBlank(row.Red) && !isBlank(row.Perfil))
    .map(row => (row.Mes ? { ...row, Mes: String(row.Mes).slice(0, 7) } : row));

/** Selector múltiple compacto con búsqueda opcional (redes, perfiles, clientes). */
function MultiSelect({ label, options, value, onChange, renderOption, emptyText, searchable = true }) {
    const [open, setOpen] = useState(false);
    const [term, setTerm] = useState('');
    const ref = useRef(null);
    useEffect(() => {
        if (!open) return undefined;
        const close = (event) => { if (ref.current && !ref.current.contains(event.target)) setOpen(false); };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [open]);
    const filtered = options.filter(opt => opt.label.toLowerCase().includes(term.trim().toLowerCase()));
    const toggle = (key) => onChange(value.includes(key) ? value.filter(k => k !== key) : [...value, key]);
    const summary = value.length === 0 ? (emptyText || `Todos los ${label.toLowerCase()}`) : value.length === 1
        ? (options.find(o => o.key === value[0])?.label || '1 seleccionado')
        : `${label}: ${value.length}`;
    return (
        <div ref={ref} style={styles.multiWrap}>
            <button type="button" onClick={() => setOpen(o => !o)} style={{ ...styles.pill, ...styles.multiBtn, ...(value.length ? styles.pillOn : {}) }} aria-expanded={open}>
                <span style={styles.multiText}>{summary}</span>
                <span aria-hidden="true" style={{ fontSize: '0.7rem' }}>▾</span>
            </button>
            {open && (
                <div style={styles.multiMenu}>
                    {searchable && (
                        <input
                            autoFocus
                            value={term}
                            onChange={e => setTerm(e.target.value)}
                            placeholder="Buscar…"
                            style={{ ...studio.controlCompact, marginBottom: 6 }}
                        />
                    )}
                    <div style={styles.multiActions}>
                        <button type="button" style={styles.linkBtn} onClick={() => onChange(filtered.map(o => o.key))}>Seleccionar {searchable ? 'visibles' : 'todos'}</button>
                        <button type="button" style={styles.linkBtn} onClick={() => onChange([])}>Limpiar</button>
                    </div>
                    <div style={styles.multiList}>
                        {filtered.map(opt => (
                            <label key={opt.key} style={styles.multiItem}>
                                <input type="checkbox" checked={value.includes(opt.key)} onChange={() => toggle(opt.key)} />
                                {renderOption ? renderOption(opt) : opt.label}
                            </label>
                        ))}
                        {!filtered.length && <p style={studio.hint}>Sin resultados.</p>}
                    </div>
                </div>
            )}
        </div>
    );
}

/** Pestañas compactas (menos alto que StudioTabs) para la barra fija del módulo. */
function CompactTabs({ items, value, onChange }) {
    return (
        <div style={styles.tabs} role="tablist">
            {items.map(item => (
                <button
                    key={item.id}
                    type="button"
                    role="tab"
                    aria-selected={item.id === value}
                    onClick={() => onChange(item.id)}
                    style={{ ...styles.tab, ...(item.id === value ? styles.tabOn : {}) }}
                >
                    {item.label}
                </button>
            ))}
        </div>
    );
}

/** Menú «Omitir»: clientes y perfiles fuera de los cálculos, con contador en el botón. */
function OmitMenu({ clientOptions, excludedClients, onClients, profileOptions, excluded, onProfiles }) {
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState('clients');
    const [term, setTerm] = useState('');
    const ref = useRef(null);
    useEffect(() => {
        if (!open) return undefined;
        const close = (event) => { if (ref.current && !ref.current.contains(event.target)) setOpen(false); };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [open]);
    const options = mode === 'clients' ? clientOptions : profileOptions;
    const value = mode === 'clients' ? excludedClients : excluded;
    const onChange = mode === 'clients' ? onClients : onProfiles;
    const filtered = options.filter(opt => `${opt.label} ${opt.red || ''}`.toLowerCase().includes(term.trim().toLowerCase()));
    return (
        <div ref={ref} style={styles.multiWrap}>
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                style={{ ...styles.pill, ...styles.multiBtn, ...(excluded.length ? styles.pillWarn : {}) }}
                aria-expanded={open}
            >
                {excluded.length ? `Omitidos: ${excluded.length}` : 'Omitir…'}
                <span aria-hidden="true" style={{ fontSize: '0.7rem' }}>▾</span>
            </button>
            {open && (
                <div style={{ ...styles.multiMenu, width: 340 }}>
                    <p style={{ ...studio.hint, margin: '0 0 8px', display: 'flex', alignItems: 'center' }}>
                        Quita clientes o perfiles de todos los cálculos y gráficas.<InfoTip term="omitir" />
                    </p>
                    <div style={{ ...styles.tabs, marginBottom: 8 }}>
                        <button type="button" onClick={() => setMode('clients')} style={{ ...styles.tab, ...(mode === 'clients' ? styles.tabOn : {}) }}>Clientes</button>
                        <button type="button" onClick={() => setMode('profiles')} style={{ ...styles.tab, ...(mode === 'profiles' ? styles.tabOn : {}) }}>Perfiles</button>
                    </div>
                    <input value={term} onChange={e => setTerm(e.target.value)} placeholder="Buscar…" style={{ ...studio.controlCompact, marginBottom: 6 }} />
                    <div style={styles.multiList}>
                        {filtered.map(opt => (
                            <label key={opt.key} style={styles.multiItem}>
                                <input
                                    type="checkbox"
                                    checked={value.includes(opt.key)}
                                    onChange={() => onChange(value.includes(opt.key) ? value.filter(k => k !== opt.key) : [...value, opt.key])}
                                />
                                <span style={styles.optLabel}>
                                    {opt.red && <NetworkDot red={opt.red} />}
                                    {opt.label}
                                    {opt.red && <span style={styles.optRed}>{opt.red}</span>}
                                </span>
                            </label>
                        ))}
                        {!filtered.length && <p style={studio.hint}>Sin resultados.</p>}
                    </div>
                    {excluded.length > 0 && (
                        <div style={{ ...styles.multiActions, marginTop: 8 }}>
                            <span style={studio.hint}>{excluded.length} perfiles omitidos</span>
                            <button type="button" style={styles.linkBtn} onClick={() => onProfiles([])}>Restaurar todo</button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

/** `embedded`: dentro del Panel admin, que ya pone su propio margen. */
const SocialRepository = ({ embedded = false }) => {
    const narrow = useIsNarrow();
    const { user } = useAuth();
    const { clients: systemClients } = useData() || {};
    const uid = user?.uid;
    const [saved] = useState(() => readView(uid));
    const [tab, setTab] = useState(saved.tab || 'summary');
    const [catalog, setCatalog] = useState(null);
    const [data, setData] = useState({ loading: true, error: '', postsMonthly: [], followersMonthly: [], followerSnapshots: [] });
    const [refreshing, setRefreshing] = useState(false);
    const [dataVersion, setDataVersion] = useState(0);
    const [networks, setNetworks] = useState(Array.isArray(saved.networks) ? saved.networks : []);
    const [profiles, setProfiles] = useState(Array.isArray(saved.profiles) ? saved.profiles : []);
    const [excluded, setExcluded] = useState(Array.isArray(saved.excluded) ? saved.excluded : []);
    const [preset, setPreset] = useState(saved.preset || 'last_3');
    const [custom, setCustom] = useState(() => {
        const current = monthKey(new Date());
        return saved.custom?.from ? saved.custom : { from: current, to: current };
    });
    const [kpiList, setKpiList] = useState(() => loadKpis(REPO, uid));
    const [kpiOpen, setKpiOpen] = useState(false);

    useEffect(() => {
        writeView(uid, { tab, networks, profiles, excluded, preset, custom });
    }, [uid, tab, networks, profiles, excluded, preset, custom]);

    const updateKpis = (list) => {
        setKpiList(list);
        saveKpis(REPO, uid, list);
    };
    /* Una fórmula que dejó de ser válida se ignora en vez de romper la pantalla. */
    const kpis = useMemo(() => kpiList.flatMap((kpi) => {
        try {
            return [prepareKpi(kpi)];
        } catch (error) {
            return [];
        }
    }), [kpiList]);

    const load = useCallback(async () => {
        setData(prev => ({ ...prev, loading: true, error: '' }));
        try {
            const results = await queryRepository(REPO, BASE_QUERIES);
            const failed = Object.values(results).find(r => !r.ok);
            if (failed) throw new Error(failed.error);
            setData({
                loading: false,
                error: '',
                postsMonthly: clean(results.postsMonthly.records),
                followersMonthly: clean(results.followersMonthly.records),
                followerSnapshots: clean(results.followerSnapshots.records),
            });
        } catch (err) {
            setData(prev => ({ ...prev, loading: false, error: err.message || 'No se pudo cargar el repositorio.' }));
        }
    }, []);

    useEffect(() => {
        load();
        fetchCatalog(REPO).then(setCatalog).catch(() => {});
    }, [load]);

    const handleRefresh = async () => {
        setRefreshing(true);
        try {
            await refreshRepository(REPO);
            await load();
            setDataVersion(v => v + 1);
        } catch (err) {
            setData(prev => ({ ...prev, error: err.message }));
        } finally {
            setRefreshing(false);
        }
    };

    const allProfiles = useMemo(
        () => profileCatalog(data.followerSnapshots, data.postsMonthly, data.followersMonthly),
        [data.followerSnapshots, data.postsMonthly, data.followersMonthly]
    );
    const availableNetworks = useMemo(() => {
        const present = new Set(allProfiles.map(p => p.red));
        return [...NETWORK_ORDER.filter(n => present.has(n)), ...Array.from(present).filter(n => !NETWORK_ORDER.includes(n))];
    }, [allProfiles]);
    const profileOptions = useMemo(
        () => allProfiles
            .filter(p => !networks.length || networks.includes(p.red))
            .map(p => ({ key: p.key, label: p.perfil, red: p.red })),
        [allProfiles, networks]
    );

    /* Al quitar una red, se quitan también sus perfiles seleccionados. */
    useEffect(() => {
        if (!networks.length) return;
        setProfiles(prev => {
            const next = prev.filter(key => networks.includes(key.split('|')[0]));
            return next.length === prev.length ? prev : next;
        });
    }, [networks]);

    const period = useMemo(() => resolvePeriod(preset, custom), [preset, custom]);
    const selection = useMemo(() => ({ networks, profiles, excluded }), [networks, profiles, excluded]);
    /* Cliente (marca) de cada perfil, para agrupar y para omitir clientes completos. */
    const clientOf = useMemo(() => clientGrouping(allProfiles, systemClients || []), [allProfiles, systemClients]);
    const clientOptions = useMemo(() => {
        const map = new Map();
        allProfiles.forEach((p) => {
            const name = clientOf.get(p.key) || p.perfil;
            if (!map.has(name)) map.set(name, []);
            map.get(name).push(p.key);
        });
        return Array.from(map.entries())
            .sort((a, b) => a[0].localeCompare(b[0], 'es'))
            .map(([name, keys]) => ({ key: name, label: `${name} (${keys.length})`, keys }));
    }, [allProfiles, clientOf]);
    const excludedClients = clientOptions.filter(c => c.keys.every(k => excluded.includes(k))).map(c => c.key);
    const setExcludedClients = (names) => {
        const keysOf = list => clientOptions.filter(c => list.includes(c.key)).flatMap(c => c.keys);
        const removed = keysOf(excludedClients.filter(name => !names.includes(name)));
        const added = keysOf(names.filter(name => !excludedClients.includes(name)));
        setExcluded(prev => Array.from(new Set([...prev.filter(k => !removed.includes(k)), ...added])));
    };
    /** Omite o restaura perfiles (lista de llaves red|perfil) desde cualquier pestaña. */
    const toggleExcluded = useCallback((keys) => {
        setExcluded((prev) => {
            const allOut = keys.every(k => prev.includes(k));
            return allOut ? prev.filter(k => !keys.includes(k)) : Array.from(new Set([...prev, ...keys]));
        });
    }, []);
    const allProfileOptions = useMemo(() => allProfiles.map(p => ({ key: p.key, label: p.perfil, red: p.red })), [allProfiles]);
    const lastSnapshot = useMemo(
        () => data.followerSnapshots.reduce((max, row) => (String(row.Fecha).slice(0, 10) > max ? String(row.Fecha).slice(0, 10) : max), ''),
        [data.followerSnapshots]
    );
    const monthlyMerged = useMemo(
        () => mergeMonthly(data.postsMonthly, data.followersMonthly),
        [data.postsMonthly, data.followersMonthly]
    );
    const previewRows = useMemo(
        () => inPeriod(monthlyMerged.filter(row => matchesSelection(row, selection)), period),
        [monthlyMerged, selection, period]
    );
    const hasFilters = networks.length > 0 || profiles.length > 0;
    /* El menú de perfiles muestra solo los de las redes elegidas. */

    return (
        <div className="cm-repo-root" style={{ ...styles.page, padding: embedded ? 0 : (narrow ? '14px 12px 28px' : '28px 32px 40px') }}>
            <style>{RESPONSIVE_CSS}</style>
            <div style={{ ...styles.stickyBar, ...(embedded ? {} : { marginLeft: narrow ? -12 : -32, marginRight: narrow ? -12 : -32, paddingLeft: narrow ? 12 : 32, paddingRight: narrow ? 12 : 32 }) }}>
                <div style={styles.titleRow}>
                    <h1 style={styles.title}>
                        Redes Sociales
                        <InfoTip
                            what="Métricas de publicaciones y seguidores de los perfiles que administramos, leídas directo de los archivos de Drive (no se copian)."
                            how={lastSnapshot ? `Última foto de seguidores: ${lastSnapshot}. «Actualizar» vuelve a leer los archivos.` : undefined}
                        />
                    </h1>
                    {!narrow && <div style={styles.tabsInline}><CompactTabs items={TABS} value={tab} onChange={setTab} /></div>}
                    <div style={styles.actions}>
                        <button type="button" onClick={() => setKpiOpen(true)} style={styles.smallBtn} title="Fórmulas personales que solo tú ves">
                            {kpis.length ? `Tus KPIs (${kpis.length})` : '+ KPI'}
                        </button>
                        {catalog?.folderUrl && (
                            <a href={catalog.folderUrl} target="_blank" rel="noreferrer" style={{ ...styles.smallBtn, textDecoration: 'none' }} title="Abrir la carpeta de Drive">
                                Drive ↗
                            </a>
                        )}
                        <button
                            type="button"
                            onClick={handleRefresh}
                            disabled={refreshing || data.loading}
                            style={{ ...styles.smallBtn, ...styles.smallPrimary, opacity: refreshing || data.loading ? 0.6 : 1 }}
                            title="Volver a leer los archivos de Drive"
                        >
                            {refreshing ? 'Actualizando…' : '↻ Actualizar'}
                        </button>
                    </div>
                </div>
                {narrow && <div style={styles.scrollRow}><CompactTabs items={TABS} value={tab} onChange={setTab} /></div>}

                {/* Sin scroll horizontal aquí: los menús desplegables se recortarían. */}
                <div style={styles.filterRow}>
                    <MultiSelect
                        label="Redes"
                        emptyText="Todas las redes"
                        options={availableNetworks.map(red => ({ key: red, label: red, red }))}
                        value={networks}
                        onChange={setNetworks}
                        searchable={false}
                        renderOption={opt => <span style={styles.optLabel}><NetworkDot red={opt.red} />{opt.label}</span>}
                    />
                    <MultiSelect
                        label="Perfiles"
                        emptyText="Todos los perfiles"
                        options={profileOptions}
                        value={profiles}
                        onChange={setProfiles}
                        renderOption={opt => (
                            <span style={styles.optLabel}><NetworkDot red={opt.red} />{opt.label}<span style={styles.optRed}>{opt.red}</span></span>
                        )}
                    />
                    <select value={preset} onChange={e => setPreset(e.target.value)} style={{ ...styles.pill, ...(preset !== 'last_3' ? styles.pillOn : {}) }} aria-label="Periodo">
                        {PERIOD_PRESETS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                    </select>
                    {preset === 'custom' && (
                        <>
                            <input type="month" value={custom.from} onChange={e => setCustom(c => ({ ...c, from: e.target.value }))} style={styles.pill} aria-label="Desde" />
                            <input type="month" value={custom.to} onChange={e => setCustom(c => ({ ...c, to: e.target.value }))} style={styles.pill} aria-label="Hasta" />
                        </>
                    )}
                    <OmitMenu
                        clientOptions={clientOptions}
                        excludedClients={excludedClients}
                        onClients={setExcludedClients}
                        profileOptions={allProfileOptions}
                        excluded={excluded}
                        onProfiles={setExcluded}
                    />
                    <span style={styles.periodText}>{periodLabel(period)}</span>
                    {(hasFilters || excluded.length > 0) && (
                        <button type="button" style={styles.linkBtn} onClick={() => { setNetworks([]); setProfiles([]); setExcluded([]); }}>Limpiar</button>
                    )}
                </div>
            </div>

            {data.error && (
                <div style={styles.error}>
                    {data.error}
                    <button type="button" onClick={load} style={{ ...studio.ghostBtn, marginLeft: 12, padding: '6px 12px' }}>Reintentar</button>
                </div>
            )}

            {tab === 'summary' && (
                <SocialSummary
                    loading={data.loading}
                    postsMonthly={data.postsMonthly}
                    followersMonthly={data.followersMonthly}
                    followerSnapshots={data.followerSnapshots}
                    monthlyMerged={monthlyMerged}
                    kpis={kpis}
                    clientOf={clientOf}
                    onToggleExclude={toggleExcluded}
                    selection={selection}
                    period={period}
                    narrow={narrow}
                />
            )}
            {tab === 'analysis' && data.loading && <LoadingBlock />}
            {tab === 'analysis' && !data.loading && (
                <SocialAnalysis
                    repo={REPO}
                    kpis={kpis}
                    clientOf={clientOf}
                    onToggleExclude={toggleExcluded}
                    followersMonthly={data.followersMonthly}
                    monthlyMerged={monthlyMerged}
                    selection={selection}
                    period={period}
                    dataVersion={dataVersion}
                    narrow={narrow}
                />
            )}
            {tab === 'followers' && data.loading && <LoadingBlock />}
            {tab === 'followers' && !data.loading && (
                <SocialFollowers
                    repo={REPO}
                    kpis={kpis}
                    clientOf={clientOf}
                    onToggleExclude={toggleExcluded}
                    followersMonthly={data.followersMonthly}
                    selection={selection}
                    period={period}
                    dataVersion={dataVersion}
                    narrow={narrow}
                />
            )}
            {tab === 'posts' && (
                <SocialPosts
                    repo={REPO}
                    kpis={kpis}
                    clientOf={clientOf}
                    onToggleExclude={toggleExcluded}
                    selection={selection}
                    period={period}
                    dataVersion={dataVersion}
                    narrow={narrow}
                />
            )}

            <KpiManager
                open={kpiOpen}
                kpis={kpis}
                onChange={updateKpis}
                onClose={() => setKpiOpen(false)}
                previewRows={previewRows}
            />
        </div>
    );
};


/* En pantallas angostas las tarjetas (studio.card) usan menos margen interno. */
const RESPONSIVE_CSS = `
@media (max-width: 720px) {
    .cm-repo-root section { padding: 16px 14px !important; border-radius: 14px !important; }
    .cm-repo-root h1 { font-size: 1.4rem !important; }
}
`;

export const styles = {
    page: { display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 1500, margin: '0 auto', boxSizing: 'border-box', width: '100%' },
    stickyBar: {
        position: 'sticky',
        top: 0,
        zIndex: 25,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: '10px 0 10px',
        backgroundColor: 'rgba(246, 245, 248, 0.96)',
        backdropFilter: 'blur(6px)',
        borderBottom: `1px solid ${ink.line}`,
    },
    titleRow: { display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 },
    title: { margin: 0, fontSize: '1.2rem', fontWeight: 650, color: ink.text, letterSpacing: '-0.02em', display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' },
    tabsInline: { flex: 1, minWidth: 0, display: 'flex', justifyContent: 'center' },
    actions: { display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto', flexShrink: 0 },
    smallBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '6px 11px',
        borderRadius: 8,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.lineStrong,
        backgroundColor: ink.surface,
        color: ink.muted,
        fontSize: '0.8rem',
        fontWeight: 600,
        fontFamily: 'inherit',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
    },
    smallPrimary: { backgroundColor: ink.accent, borderColor: ink.accent, color: '#ffffff' },
    tabs: { display: 'inline-flex', gap: 2, padding: 3, borderRadius: 10, backgroundColor: '#ece9f3' },
    tab: {
        padding: '5px 13px',
        borderRadius: 8,
        borderWidth: 0,
        borderStyle: 'solid',
        borderColor: 'transparent',
        backgroundColor: 'transparent',
        color: ink.muted,
        fontSize: '0.82rem',
        fontWeight: 600,
        fontFamily: 'inherit',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
    },
    tabOn: { backgroundColor: ink.surface, color: ink.accentText, boxShadow: '0 1px 3px rgba(28, 29, 33, 0.12)' },
    filterRow: { display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' },
    scrollRow: { overflowX: 'auto', flexWrap: 'nowrap', WebkitOverflowScrolling: 'touch', paddingBottom: 2 },
    pill: {
        height: 32,
        padding: '0 12px',
        borderRadius: 999,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.lineStrong,
        backgroundColor: ink.surface,
        color: ink.text,
        fontSize: '0.8rem',
        fontFamily: 'inherit',
        cursor: 'pointer',
        boxSizing: 'border-box',
        flexShrink: 0,
    },
    pillOn: { borderColor: ink.accent, backgroundColor: ink.accentSoft, color: ink.accentText },
    pillWarn: { borderColor: '#f3d58a', backgroundColor: '#fef7e0', color: '#8a6100' },
    periodText: { fontSize: '0.78rem', color: ink.faint, fontWeight: 600, whiteSpace: 'nowrap', marginLeft: 4 },
    multiWrap: { position: 'relative', flexShrink: 0 },
    multiBtn: { display: 'inline-flex', alignItems: 'center', gap: 8, maxWidth: 220 },
    multiText: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    multiMenu: {
        position: 'absolute',
        top: 'calc(100% + 6px)',
        left: 0,
        zIndex: 40,
        width: 300,
        maxWidth: 'calc(100vw - 32px)',
        padding: 10,
        backgroundColor: ink.surface,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.line,
        borderRadius: 12,
        boxShadow: '0 12px 30px rgba(28, 29, 33, 0.16)',
        boxSizing: 'border-box',
    },
    multiActions: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 },
    multiList: { maxHeight: 280, overflowY: 'auto', display: 'flex', flexDirection: 'column' },
    multiItem: { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 4px', fontSize: '0.84rem', cursor: 'pointer', color: ink.text },
    optLabel: { display: 'inline-flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0 },
    optRed: { marginLeft: 'auto', fontSize: '0.72rem', color: ink.faint },
    linkBtn: {
        background: 'none',
        border: 'none',
        padding: '4px 2px',
        color: ink.accent,
        fontWeight: 600,
        fontSize: '0.8rem',
        fontFamily: 'inherit',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
    },
    error: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        padding: '12px 14px',
        borderRadius: 12,
        backgroundColor: ink.dangerSoft,
        color: ink.danger,
        fontSize: '0.88rem',
    },
};

export default SocialRepository;
